import React, { ChangeEvent } from 'react'
import * as S from './styles'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  formatTelephoneWithParenthesis,
  phoneMaskParenthesis
} from 'presentation/utils/masks'
import { FormikProvider, useFormik, useFormikContext } from 'formik'
import CheckboxNew from 'presentation/shared/components/CheckboxNew'
import { PendencyType } from 'domain/entities/surgical-pendency'
import { ActionsPendency } from 'common/enum/actions-pendency'
import { SurgicalOrderModel } from 'domain/entities/surgical-order-model'
import { validationSchema } from './validationSchema'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { useServices } from 'presentation/hooks/use-services'
import ContainerNew from 'presentation/hospital/components/ContainerNew'
import { Grid } from '@material-ui/core'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import TabsNew from 'presentation/shared/components/TabsNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import PendencyInitialDocumentTab from './PendencyTabs/PendencyInitialDocumentTab'
import PendencyAfterOperatorRequest from './PendencyTabs/PendencyAfterOperatorRequest'
import PendencyPersonalized from './PendencyTabs/PendencyPersonalized'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import HeadingNew from 'presentation/shared/components/HeadingNew'

type Props = {
  surgicalOrder: SurgicalOrderModel
} & WithLoadingProps

export type CreatePendencyFormValues = {
  notify_doctor: boolean
  notify_secretary: boolean
  notify_patient: boolean
  doctor_name?: string
  email_doctor?: string
  email_secretary?: string
  email_patient?: string
  phone_doctor?: string
  phone_secretary?: string
  phone_patient?: string
  action?: string
  selected_options?: string[]
  OPTION_OTHERS_TEXT?: string
  OPTION_SUPPLEMENTARY_MEDICAL_REPORT_TEXT?: string
  OPTION_EXAM_REPORTS_PHOTOS_TEXT?: string
  OPTION_SUPPLIERS_TEXT?: string
  OPTION_PERSONALIZED_TEXT?: string
  general_observations?: string
  // Dynamical fields for complementary texts
  [key: string]: any
}

type PendencyTab = {
  label: string
  componentChildren: JSX.Element
  type: ActionsPendency
}

export type PendencyTabProps = {
  title: string
  handleCheckboxChange?: (event: ChangeEvent<HTMLInputElement>) => void
  isOptionChecked?: (doc: string) => boolean
}

export const CreatePendencyLayout = WithLoading(
  ({ surgicalOrder, setIsLoading }: Props) => {
    const history = useHistory()
    const surgicalPendencyService = useServices().surgicalPendency
    const doctorService = useServices().doctor
    const userService = useServices().user

    const updateDoctorPhone = async (newPhone: string) => {
      const newDoctorPhone = newPhone.replace(/\D+/g, '')
      const currentDoctorPhone =
        surgicalOrder?.doctor?.phone?.replace(/\D+/g, '') || ''

      if (
        surgicalOrder?.doctor?.doctor_id &&
        newDoctorPhone &&
        newDoctorPhone !== currentDoctorPhone
      ) {
        try {
          await doctorService.updateDoctor({
            doctor_id: surgicalOrder.doctor.doctor_id,
            data: {
              user: { phone: newDoctorPhone }
            }
          })
        } catch (e) {
          toast.error('Erro ao atualizar numero de telefone do médico')
        }
      }
    }

    const updateSecretaryPhone = async (newPhone: string) => {
      const secretary = surgicalOrder?.doctor?.secretary?.[0]
      const newSecretaryPhone = newPhone.replace(/\D+/g, '')
      const currentSecretaryPhone = secretary?.phone?.replace(/\D+/g, '') || ''

      if (
        secretary?.user_id &&
        newSecretaryPhone &&
        newSecretaryPhone !== currentSecretaryPhone
      ) {
        try {
          await userService.updateUser({
            user_id: secretary.user_id,
            phone: newSecretaryPhone
          })
        } catch (e) {
          toast.error('Erro ao atualizar numero de telefone da secretária')
        }
      }
    }

    const formik = useFormik<CreatePendencyFormValues>({
      initialValues: {
        notify_doctor: false,
        notify_secretary: false,
        notify_patient: false,
        doctor_name: surgicalOrder?.doctor?.name || '',
        phone_doctor: surgicalOrder?.doctor?.phone || '',
        phone_secretary: surgicalOrder?.doctor?.secretary[0]?.phone || '',
        phone_patient: surgicalOrder?.patient?.phone || '',
        email_doctor: surgicalOrder?.doctor?.email || '',
        email_secretary: surgicalOrder?.doctor?.secretary[0]?.email || '',
        email_patient: surgicalOrder?.patient?.email || '',
        action: ActionsPendency.CRMO_CREATED_PENDENCY_HEALTH_INSURANCE,
        selected_options: [],
        OPTION_OTHERS_TEXT: '',
        OPTION_SUPPLEMENTARY_MEDICAL_REPORT_TEXT: '',
        OPTION_EXAM_REPORTS_PHOTOS_TEXT: '',
        OPTION_SUPPLIERS_TEXT: '',
        OPTION_PERSONALIZED_TEXT: '',
        general_observations: ''
      },
      validationSchema,
      validateOnMount: true,
      enableReinitialize: true,
      onSubmit: async (values) => {
        setIsLoading(true)
        try {
          const isNotifySelected = values.notify_doctor || values.notify_patient
          const notifyDoctor = isNotifySelected ? values.notify_doctor : true
          const notifySecretary = notifyDoctor
          const notifyPatient = isNotifySelected ? values.notify_patient : false

          const contactsToUpdate = []
          if (notifyDoctor) {
            contactsToUpdate.push(updateDoctorPhone(values.phone_doctor || ''))
          }
          if (notifySecretary) {
            contactsToUpdate.push(
              updateSecretaryPhone(values.phone_secretary || '')
            )
          }

          if (contactsToUpdate.length) {
            await Promise.all(contactsToUpdate)
          }

          const payload = {
            doctor_id: surgicalOrder.doctor?.doctor_id,
            patient_id: surgicalOrder.patient?.patient_id,
            notify: {
              doctor: notifyDoctor,
              secretary: notifySecretary,
              patient: notifyPatient
            },
            notifyBy: {
              whatsapp: true,
              email: true,
              platform: true
            },
            notifyPatientBy: {
              whatsapp: false,
              email: true,
              platform: true
            },
            email: {
              doctor: values.email_doctor,
              secretary: values.email_secretary,
              patient: values.email_patient
            },
            phone: {
              doctor: values.phone_doctor
                ? values.phone_doctor.replace(/\D+/g, '')
                : '',
              patient: values.phone_patient
                ? values.phone_patient.replace(/\D+/g, '')
                : '',
              secretary: values.phone_secretary
                ? values.phone_secretary.replace(/\D+/g, '')
                : ''
            },
            surgical_order_id: surgicalOrder.surgical_order_id!,
            type: PendencyType.AUTHORIZATION,
            action: values.action,
            observation: values.general_observations
          }
          await surgicalPendencyService.addSurgicalPendency(payload)
          toast.success('Pendência criada com sucesso')
          history.goBack()
        } catch (error: any) {
          toast.error('Ocorreu um erro ao criar pendência')
        } finally {
          setIsLoading(false)
        }
      }
    })

    return (
      <ContainerNew title="Gerar pendência">
        <FormikProvider value={formik}>
          <NotificationOptions />
          <NotificationMessageTabs />
          <ButtonsContainer />
        </FormikProvider>
      </ContainerNew>
    )
  }
)

const NotificationOptions = () => {
  const { handleChange, values, errors, touched, handleBlur } =
    useFormikContext<CreatePendencyFormValues>()

  return (
    <Grid container>
      <Grid item sm={4}>
        <SupportTextNew
          color="black"
          size="medium"
          weight="bold"
          style={{ margin: '24px 0px' }}
        >
          Para quem deseja enviar a pendência?
        </SupportTextNew>
        <S.CheckboxWrapper>
          <CheckboxNew
            label="Médico/Secretária"
            labelFor="notify_doctor"
            name="notify_doctor"
            checked={values.notify_doctor}
            onChange={handleChange}
          />
          <CheckboxNew
            label="Paciente"
            labelFor="notify_patient"
            name="notify_patient"
            checked={values.notify_patient}
            onChange={handleChange}
          />
        </S.CheckboxWrapper>
      </Grid>

      <Grid container item sm={8} style={{ display: 'flex', gap: '40px' }}>
        <S.CardInput style={{ flex: 1 }}>
          <HeadingNew as="h3" color="neutral700" size="small" weight="bold">
            Médico (a)
          </HeadingNew>
          <TextFieldNew
            id="email"
            name="email_doctor"
            label="E-mail"
            labelOverlapping
            labelSize="xxsmall"
            placeholder="Insira o e-mail aqui"
            value={values.email_doctor}
            error={touched.email_doctor && errors.email_doctor}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <TextFieldNew
            id="fone"
            name="phone_doctor"
            label="Whatsapp"
            labelOverlapping
            labelSize="xxsmall"
            placeholder="+55 (00) 00000-0000"
            mask={phoneMaskParenthesis}
            value={formatTelephoneWithParenthesis(values.phone_doctor)}
            error={touched.phone_doctor && errors.phone_doctor}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </S.CardInput>

        <S.CardInput style={{ flex: 1 }}>
          <HeadingNew as="h3" color="neutral700" size="small" weight="bold">
            Secretário (a)
          </HeadingNew>
          <TextFieldNew
            id="email"
            name="email_secretary"
            label="E-mail"
            labelOverlapping
            labelSize="xxsmall"
            placeholder="Insira o e-mail aqui"
            value={values.email_secretary}
            error={touched.email_secretary && errors.email_secretary}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <TextFieldNew
            id="fone"
            name="phone_secretary"
            label="Whatsapp"
            labelOverlapping
            labelSize="xxsmall"
            placeholder="+55 (00) 00000-0000"
            mask={phoneMaskParenthesis}
            value={formatTelephoneWithParenthesis(values.phone_secretary)}
            error={touched.phone_secretary && errors.phone_secretary}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </S.CardInput>

        <S.CardInput style={{ flex: 1 }}>
          <HeadingNew as="h3" color="neutral700" size="small" weight="bold">
            Paciente
          </HeadingNew>
          <TextFieldNew
            id="email"
            name="email_patient"
            label="E-mail"
            labelOverlapping
            labelSize="xxsmall"
            placeholder="Insira o e-mail aqui"
            value={values.email_patient}
            error={touched.email_patient && errors.email_patient}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <TextFieldNew
            id="fone"
            name="phone_patient"
            label="Whatsapp"
            labelOverlapping
            labelSize="xxsmall"
            placeholder="+55 (11) 00000-0000"
            mask={phoneMaskParenthesis}
            value={formatTelephoneWithParenthesis(values.phone_patient)}
            error={touched.phone_patient && errors.phone_patient}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </S.CardInput>
      </Grid>
    </Grid>
  )
}

const NotificationMessageTabs = () => {
  const { values, setFieldValue } = useFormikContext<CreatePendencyFormValues>()

  const isOptionChecked = (doc: string) => {
    return values?.selected_options?.includes(doc) || false
  }

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target
    const currentList = values.selected_options || []

    if (checked) {
      if (!currentList.includes(value)) {
        setFieldValue('selected_options', [...currentList, value])
      }
    } else {
      setFieldValue(
        'selected_options',
        currentList.filter((doc: string) => doc !== value)
      )
    }
  }

  const tabItems: PendencyTab[] = [
    {
      label: 'Pendência de documentação inicial',
      componentChildren: (
        <PendencyInitialDocumentTab
          title="Pendência de documentação inicial"
          handleCheckboxChange={handleCheckboxChange}
          isOptionChecked={isOptionChecked}
        />
      ),
      type: ActionsPendency.CRMO_CREATED_PENDENCY_HEALTH_INSURANCE
    },
    {
      label: 'Pendência de documentação após solicitação na operadora',
      componentChildren: (
        <PendencyAfterOperatorRequest
          title="Pendência de documentação após solicitação na operadora"
          handleCheckboxChange={handleCheckboxChange}
          isOptionChecked={isOptionChecked}
        />
      ),
      type: ActionsPendency.CRMO_CREATED_PENDENCY_PROVIDER
    },
    {
      label: 'Pendência personalizada',
      componentChildren: (
        <PendencyPersonalized title="Pendência personalizada" />
      ),
      type: ActionsPendency.CRMO_CREATED_PENDENCY_PERSONALIZED
    }
  ]

  const handleChangeTab = async (newTabIndex: number) => {
    const newPendencyTab = tabItems.find(
      (pendency, idx) => pendency && idx === newTabIndex
    )
    if (newPendencyTab) {
      setFieldValue('selected_options', [])
      setFieldValue('action', newPendencyTab.type)
    }
  }

  return (
    <div style={{ marginTop: '16px' }}>
      <HeadingNew as="h4" color="black" size="medium" weight="bold">
        Tipo de pendência{' '}
        <SupportTextNew
          as="small"
          color="neutral800"
          size="xxsmall"
          weight="normal"
        >
          (Selecione o tipo pendência para visualizar as mensagens padronizadas
          relacionadas. Se necessário adicione texto complementar no campo de
          texto.)
        </SupportTextNew>
      </HeadingNew>

      <div style={{ marginTop: '8px' }}>
        <TabsNew
          tabItems={tabItems}
          tabsMaxWidth="80%"
          borderColor="neutral100"
          // defaultTab={defaultTab}
          onChangeTab={(tabIndex) => handleChangeTab(tabIndex)}
        />
      </div>
    </div>
  )
}

const ButtonsContainer = () => {
  const { submitForm, isSubmitting, isValid } =
    useFormikContext<CreatePendencyFormValues>()
  const history = useHistory()

  return (
    <S.Buttons>
      <ButtonNew
        variant="text"
        noPadding
        onClick={history.goBack}
        style={{ width: '120px' }}
      >
        Cancelar
      </ButtonNew>
      <ButtonNew
        data-testid="create-pendency-button"
        onClick={submitForm}
        disabled={isSubmitting || !isValid}
        style={{ width: '186px' }}
        noPadding
      >
        Gerar Pendência
      </ButtonNew>
    </S.Buttons>
  )
}
